import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import config from '../../config';
import Reveal from './Reveal';
import { LANDING_FONT, LC, MOTION, PAD_X, r } from './landingTokens';

const badgeSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: r(10),
  border: `${r(1)} solid rgba(255,255,255,.22)`,
  borderRadius: r(12),
  p: `${r(9)} ${r(16)}`,
  textDecoration: 'none',
  background: 'rgba(255,255,255,.06)',
  transition: 'background 150ms ease',
  '&:hover': { background: 'rgba(255,255,255,.12)' },
};
const smallSx = { fontFamily: LANDING_FONT, fontSize: r(9.5), fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: LC.mint };
const bigSx = { fontFamily: LANDING_FONT, fontWeight: 800, fontSize: r(14), color: 'primary.contrastText', letterSpacing: '-0.01em' };

export default function AppDownload() {
  // The store marks are SVG `fill` attributes, which `sx` does not resolve
  // against the palette, so the contrast value is read from the theme directly.
  const theme = useTheme();
  const onDark = theme.palette.primary.contrastText;

  return (
    <Box
      component="section"
      id="try"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
        background: [
          `radial-gradient(40% 50% at 90% 110%, rgba(10,156,95,.3), transparent 70%)`,
          `radial-gradient(125% 130% at 16% -12%, ${LC.heroMid} 0%, ${LC.evergreen} 50%, ${LC.heroDark} 100%)`,
        ].join(', '),
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', maxWidth: r(720), px: PAD_X, py: 'clamp(4rem, 9vw, 6.75rem)', textAlign: 'center' }}>
        <Reveal repeat>
          <Typography component="h2" sx={{ fontFamily: LANDING_FONT, fontSize: 'clamp(2rem, 5vw, 3.25rem)', fontWeight: 800, letterSpacing: '-0.035em', color: 'primary.contrastText', lineHeight: 1.02, textWrap: 'balance' }}>
            Take your stacks anywhere.
          </Typography>
        </Reveal>
        <Reveal delay={MOTION.stagger} repeat>
          <Typography sx={{ fontFamily: LANDING_FONT, fontSize: 'clamp(0.9375rem, 2vw, 1.0625rem)', color: LC.heroLead, mt: r(16), mx: 'auto', maxWidth: '40ch', lineHeight: 1.55 }}>
            Your evidence-backed stack, on your phone — synced and screened wherever you go.
          </Typography>
        </Reveal>
        <Reveal delay={MOTION.stagger * 2} repeat sx={{ mt: 'clamp(1.75rem, 3vw, 2.125rem)', display: 'flex', gap: r(12), justifyContent: 'center', flexWrap: 'wrap' }}>
          <Box component="a" href={config.APP_STORE_IOS} target="_blank" rel="noopener noreferrer" aria-label="Download on the App Store" sx={badgeSx}>
            <Box component="svg" width={18} height={18} viewBox="0 0 24 24" fill={onDark} aria-hidden="true">
              <path d="M17.564 12.74c-.024-2.69 2.196-3.98 2.296-4.045-1.252-1.83-3.196-2.08-3.886-2.106-1.653-.168-3.23.974-4.07.974-.838 0-2.133-.95-3.51-.924-1.806.027-3.473 1.05-4.402 2.668-1.878 3.256-.48 8.073 1.347 10.718.893 1.295 1.958 2.747 3.354 2.695 1.347-.054 1.854-.87 3.48-.87 1.626 0 2.083.87 3.507.843 1.448-.026 2.366-1.32 3.252-2.62 1.025-1.503 1.448-2.957 1.473-3.032-.032-.014-2.827-1.086-2.851-4.305zM14.96 4.51c.742-.9 1.243-2.15 1.106-3.394-1.07.043-2.366.712-3.133 1.611-.688.797-1.29 2.07-1.128 3.29 1.193.092 2.413-.607 3.155-1.507z" />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', lineHeight: 1.15 }}>
              <Box component="span" sx={smallSx}>Download on the</Box>
              <Box component="span" sx={bigSx}>App Store</Box>
            </Box>
          </Box>
          <Box component="a" href={config.APP_STORE_ANDROID} target="_blank" rel="noopener noreferrer" aria-label="Get it on Google Play" sx={badgeSx}>
            <Box component="svg" width={17} height={17} viewBox="0 0 24 24" aria-hidden="true">
              <path d="M3.6 1.84a1.4 1.4 0 0 0-.6 1.16v18a1.4 1.4 0 0 0 .6 1.16l.07.04L13.2 12.1v-.2L3.67 1.8l-.07.04z" fill={LC.mint} />
              <path d="M16.5 15.4 13.2 12.1v-.2l3.3-3.3.08.05 3.9 2.22c1.12.63 1.12 1.66 0 2.3l-3.9 2.22-.08.05z" fill={LC.accent} />
              {/* No brief-locked value reproduces this facet's old mint, so it
                  takes the nearest one and now matches the left wedge. */}
              <path d="m16.58 15.35-3.38-3.35-9.6 9.6c.37.39.98.44 1.67.05l11.31-6.3z" fill={LC.mint} />
              <path d="M16.58 8.65 5.27 2.35c-.69-.39-1.3-.34-1.67.05l9.6 9.6 3.38-3.35z" fill={onDark} />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'left', lineHeight: 1.15 }}>
              <Box component="span" sx={smallSx}>Get it on</Box>
              <Box component="span" sx={bigSx}>Google Play</Box>
            </Box>
          </Box>
        </Reveal>
      </Box>
    </Box>
  );
}
